import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";

import { Icon } from "../../components/Icon";
import {
  AdAvatar,
  Callout,
  Drawer,
  Empty,
  Field,
  KeyValue,
  Pill,
  Section,
  ToggleRow,
  errText,
  fmtDateTime,
  useToast,
} from "../ui";
import {
  api,
  type AdminClientDetail,
  type AdminRole,
  type AdminUserRow,
  type AutotaskContactCandidate,
  type Me,
} from "../../services/api";

/** The client-side roles an admin can assign here. Staff roles live on the
 *  iQuest staff page and are refused by the API from this surface. */
const ROLE_CARDS: { value: AdminRole; label: string; blurb: string }[] = [
  {
    value: "client_admin",
    label: "Client admin",
    blurb: "Manages users, sees everything, approves",
  },
  {
    value: "approver",
    label: "Approver",
    blurb: "Own tickets, plus named in approval chains",
  },
  { value: "end_user", label: "User", blurb: "Own tickets and requests" },
];

/** Grants, which no role implies. The API keeps the authoritative allow-list. */
const PERMISSIONS: { value: string; label: string; blurb: string }[] = [
  {
    value: "view_all_company_tickets",
    label: "View all company tickets",
    blurb: "See every ticket for the organisation, not just their own.",
  },
  {
    value: "view_reports",
    label: "View reports",
    blurb: "Open the Reports and SLA dashboards.",
  },
];

export function roleLabel(role: string): string {
  switch (role) {
    case "iquest_admin":
      return "iQuest admin";
    case "service_desk":
      return "Service desk";
    case "client_admin":
      return "Client admin";
    case "approver":
      return "Approver";
    case "end_user":
      return "User";
    default:
      return role;
  }
}

function statusPill(user: AdminUserRow) {
  if (user.status === "active") {
    return user.last_login_at ? (
      <Pill kind="ok">Active</Pill>
    ) : (
      <Pill kind="warn">Invited</Pill>
    );
  }
  return <Pill kind="off">{user.status === "removed" ? "Deleted" : "Deactivated"}</Pill>;
}

/**
 * Users & access.
 *
 * The list is read-only; every change happens in the person drawer.
 * Permission toggles apply immediately and are audited on their own.
 * Name, work email (only before sign-in), and role save together from the
 * drawer footer, each through its own audited API call.
 */
export function UsersSection({
  client,
  me,
  onChanged,
}: {
  client: AdminClientDetail;
  me: Me;
  onChanged: () => void;
}) {
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<AdminUserRow | null>(null);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);

  const { data, mutate, isLoading, error } = useSWR(
    ["/api/admin/users", client.id, showInactive],
    () =>
      api.admin.users.list({
        company_id: client.id,
        include_inactive: showInactive,
        page: 1,
        page_size: 200,
      }),
    { keepPreviousData: true },
  );

  const users = useMemo(() => data?.items ?? [], [data]);

  async function refresh() {
    await mutate();
    onChanged();
  }

  return (
    <>
      <Section
        title="People"
        desc={`${users.length} ${users.length === 1 ? "person" : "people"} at ${client.name}. Open a row to correct a name, or a work email before they have signed in. Role and permission changes are audited.`}
        flush
        actions={
          <>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setImporting(true)}
            >
              <Icon name="refresh" size={15} /> Import from Autotask
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setAdding(true)}
            >
              <Icon name="userPlus" size={15} /> Add person
            </button>
          </>
        }
      >
        <label
          className="checkbox-row"
          style={{ margin: "0 22px 12px", fontSize: 12.5 }}
        >
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          <span>Show deactivated people</span>
        </label>
        {error ? (
          <div className="form-error" style={{ margin: 18 }}>
            {String((error as Error).message ?? error)}
          </div>
        ) : isLoading ? (
          <div style={{ padding: 18 }}>Loading people…</div>
        ) : users.length === 0 ? (
          <Empty
            title="No portal users yet"
            blurb="Add the first person, or import contacts from Autotask."
          />
        ) : (
          <div className="ad-tablewrap">
            <table className="ad-table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Role</th>
                  <th>Permissions</th>
                  <th>Last sign-in</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr
                    key={u.id}
                    className="clickable"
                    style={u.status === "active" ? undefined : { opacity: 0.6 }}
                    onClick={() => setEditing(u)}
                  >
                    <td>
                      <span className="who">
                        <AdAvatar name={u.display_name || u.email} />
                        <span>
                          <span className="n">{u.display_name}</span>
                          <br />
                          <span className="e">{u.email}</span>
                        </span>
                      </span>
                    </td>
                    <td className="nowrap">{roleLabel(u.role)}</td>
                    <td>
                      {u.permissions.length === 0 ? (
                        <span style={{ color: "var(--iq-text-faint)" }}>
                          Own tickets only
                        </span>
                      ) : (
                        u.permissions.map((p) => (
                          <span key={p} className="ad-scope">
                            {PERMISSIONS.find((x) => x.value === p)?.label ?? p}
                          </span>
                        ))
                      )}
                    </td>
                    <td className="mono nowrap">{fmtDateTime(u.last_login_at)}</td>
                    <td className="nowrap">{statusPill(u)}</td>
                    <td className="r">
                      <span style={{ color: "var(--iq-text-disabled)", display: "inline-flex" }}>
                        <Icon name="chevRight" size={16} />
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="How roles work">
        <KeyValue
          k="Client admin"
          v="Manages their own users, sees all company tickets and reports, approves requests."
        />
        <KeyValue
          k="Approver"
          v="Raises their own tickets, and can be named as an approver in routing rules."
        />
        <KeyValue
          k="User"
          v="Raises and tracks their own tickets and requests. Extra permissions are granted per person."
        />
      </Section>

      {adding && (
        <AddPersonDrawer
          client={client}
          onClose={() => setAdding(false)}
          onSaved={async () => {
            setAdding(false);
            await refresh();
          }}
        />
      )}

      {editing && (
        <PersonDrawer
          client={client}
          me={me}
          user={editing}
          onClose={() => setEditing(null)}
          onChanged={refresh}
        />
      )}

      {importing && (
        <ImportDrawer
          client={client}
          onClose={() => setImporting(false)}
          onImported={refresh}
        />
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Add person                                                         */
/* ------------------------------------------------------------------ */

function AddPersonDrawer({
  client,
  onClose,
  onSaved,
}: {
  client: AdminClientDetail;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<AdminRole>("end_user");
  const [permissions, setPermissions] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!name.trim() || !email.trim()) {
      setError("Name and email are both required.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.admin.users.create({
        email: email.trim(),
        display_name: name.trim(),
        role,
        company_id: client.id,
        permissions,
      });
      await onSaved();
      toast(`Invitation sent to ${email.trim()}`);
    } catch (e) {
      setError(errText(e, "Could not create the account."));
      setBusy(false);
    }
  }

  return (
    <Drawer
      eyebrow={client.name}
      title="Add person"
      desc="They sign in with their Microsoft account; the portal matches on email."
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void submit()}
            disabled={busy}
          >
            {busy ? "Sending…" : "Send invitation"}
          </button>
        </>
      }
    >
      <div className="ad-grid">
        <Field label="Full name">
          <input value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Work email">
          <input
            type="email"
            value={email}
            maxLength={320}
            placeholder={`name@${client.allowed_email_domains[0] ?? "company.com.au"}`}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <div className="ad-field span2">
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label>Role</label>
          <RoleCards value={role} onChange={setRole} />
        </div>
      </div>

      <div className="ad-eyebrow" style={{ margin: "20px 0 4px" }}>
        Extra permissions
      </div>
      {PERMISSIONS.map((p) => (
        <ToggleRow
          key={p.value}
          label={p.label}
          sub={p.blurb}
          checked={permissions.includes(p.value)}
          onChange={(on) =>
            setPermissions((prev) =>
              on ? [...prev, p.value] : prev.filter((x) => x !== p.value),
            )
          }
        />
      ))}

      {error && (
        <div className="form-error" style={{ marginTop: 12 }}>
          {error}
        </div>
      )}
    </Drawer>
  );
}

/* ------------------------------------------------------------------ */
/*  Edit person                                                        */
/* ------------------------------------------------------------------ */

export function PersonDrawer({
  client,
  me,
  user,
  onClose,
  onChanged,
}: {
  client: AdminClientDetail;
  me: Me;
  user: AdminUserRow;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const toast = useToast();
  const isSelf = user.id === me.id;
  // Strict equality: a list response from an older API omits the flag, and
  // that must stay locked rather than offer an edit the server will refuse.
  const emailEditable = user.email_editable === true;
  const [name, setName] = useState(user.display_name);
  const [email, setEmail] = useState(user.email);
  const [role, setRole] = useState<AdminRole>(user.role as AdminRole);
  const [permissions, setPermissions] = useState<string[]>(user.permissions);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(user.display_name);
    setEmail(user.email);
    setRole(user.role as AdminRole);
    setPermissions(user.permissions);
  }, [user]);

  const inactive = user.status !== "active";

  async function togglePermission(permission: string, on: boolean) {
    if (isSelf) {
      setError("You cannot change your own permissions.");
      return;
    }
    const before = permissions;
    setPermissions(on ? [...permissions, permission] : permissions.filter((p) => p !== permission));
    setError(null);
    try {
      if (on) await api.admin.users.grant(user.id, permission);
      else await api.admin.users.revoke(user.id, permission);
      await onChanged();
      toast(on ? "Permission granted" : "Permission revoked");
    } catch (e) {
      setPermissions(before);
      setError(errText(e, "Could not update that permission."));
    }
  }

  async function save() {
    const nextName = name.trim();
    const nextEmail = email.trim();
    if (!nextName || (emailEditable && !nextEmail)) {
      setError("Name and email are both required.");
      return;
    }
    const nameChanged = nextName !== user.display_name;
    const emailChanged = emailEditable && nextEmail.toLowerCase() !== user.email.toLowerCase();
    const roleChanged = role !== user.role;
    if (!nameChanged && !emailChanged && !roleChanged) {
      onClose();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (nameChanged || emailChanged) {
        await api.admin.users.updateProfile(user.id, {
          ...(nameChanged ? { display_name: nextName } : {}),
          ...(emailChanged ? { email: nextEmail } : {}),
        });
      }
      if (roleChanged) {
        await api.admin.users.setRole(user.id, role);
      }
      await onChanged();
      toast(`Saved ${nextName}`);
      onClose();
    } catch (e) {
      setError(errText(e, "Could not save these details."));
      setBusy(false);
    }
  }

  async function toggleStatus() {
    const next = inactive ? "active" : "suspended";
    if (
      next === "suspended" &&
      !window.confirm(
        `Deactivate ${user.display_name}? They lose sign-in immediately; their history is kept and you can reactivate them any time.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.admin.users.setStatus(user.id, next);
      await onChanged();
      toast(next === "active" ? `${user.display_name} reactivated` : `${user.display_name} deactivated`);
      onClose();
    } catch (e) {
      setError(errText(e, "Could not update this account."));
      setBusy(false);
    }
  }

  return (
    <Drawer
      eyebrow={client.name}
      title={user.display_name}
      desc={`${user.email} · last sign-in ${fmtDateTime(user.last_login_at)}`}
      onClose={onClose}
      footer={
        <>
          {!isSelf && (
            <button
              type="button"
              className={`btn btn-sm left ${inactive ? "btn-ghost" : "btn-danger"}`}
              onClick={() => void toggleStatus()}
              disabled={busy}
            >
              {inactive ? "Reactivate" : "Deactivate"}
            </button>
          )}
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void save()}
            disabled={busy || isSelf}
          >
            {busy ? "Saving…" : "Save changes"}
          </button>
        </>
      }
    >
      {isSelf && (
        <div style={{ marginBottom: 14 }}>
          <Callout kind="warn">
            You cannot change your own details, role, or permissions. Ask another admin.
          </Callout>
        </div>
      )}

      <div className="ad-grid">
        <Field
          label="Full name"
          note="Shown in the portal. A later sign-in does not replace a name saved here."
        >
          <input
            value={name}
            maxLength={200}
            readOnly={isSelf}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field
          label="Work email"
          note={
            emailEditable
              ? "Correct a typo before they sign in. The portal matches their Microsoft account on this address."
              : "Locked after sign-in, because this address is how Microsoft matching works. Deactivate this person and add them again with the correct address."
          }
        >
          <input
            type="email"
            value={email}
            maxLength={320}
            readOnly={!emailEditable || isSelf}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <div className="ad-field span2">
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label>Role</label>
          <RoleCards value={role} onChange={setRole} disabled={isSelf} />
        </div>
      </div>

      <div className="ad-eyebrow" style={{ margin: "20px 0 4px" }}>
        Extra permissions
      </div>
      <div className="ad-note" style={{ marginBottom: 4 }}>
        These apply the moment you switch them, and each one is audited on its own.
      </div>
      {PERMISSIONS.map((p) => (
        <ToggleRow
          key={p.value}
          label={p.label}
          sub={p.blurb}
          checked={permissions.includes(p.value)}
          disabled={isSelf}
          onChange={(on) => void togglePermission(p.value, on)}
        />
      ))}

      {error && (
        <div className="form-error" style={{ marginTop: 12 }}>
          {error}
        </div>
      )}
    </Drawer>
  );
}

function RoleCards({
  value,
  onChange,
  disabled,
}: {
  value: AdminRole;
  onChange: (v: AdminRole) => void;
  disabled?: boolean;
}) {
  return (
    <div className="ad-sev" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
      {ROLE_CARDS.map((r) => (
        <label key={r.value}>
          <input
            type="radio"
            name="person-role"
            value={r.value}
            checked={value === r.value}
            disabled={disabled}
            onChange={() => onChange(r.value)}
          />
          <span className="t">{r.label}</span>
          <span className="s">{r.blurb}</span>
        </label>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Import from Autotask                                               */
/* ------------------------------------------------------------------ */

function ImportDrawer({
  client,
  onClose,
  onImported,
}: {
  client: AdminClientDetail;
  onClose: () => void;
  onImported: () => Promise<void>;
}) {
  const toast = useToast();
  const { data, isLoading, error } = useSWR(
    ["/api/admin/clients", client.id, "autotask-contacts"],
    () => api.admin.clients.autotaskContacts.list(client.id),
  );

  const [selected, setSelected] = useState<Set<number> | null>(null);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const contacts = useMemo(() => data?.contacts ?? [], [data]);
  const importable = useMemo(
    () => contacts.filter((c) => c.importable && !c.already_user),
    [contacts],
  );

  // Everything importable is pre-ticked once the preview lands: the common
  // case is "bring them all in", and un-ticking is cheaper than ticking.
  useEffect(() => {
    if (data && selected === null) {
      setSelected(new Set(importable.map((c) => c.autotask_contact_id)));
    }
  }, [data, importable, selected]);

  const chosen = selected ?? new Set<number>();
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return importable;
    return importable.filter(
      (c) =>
        (c.full_name ?? "").toLowerCase().includes(q) ||
        (c.email ?? "").toLowerCase().includes(q),
    );
  }, [importable, query]);

  const notLinked = data != null && data.autotask_company_id == null;

  async function run() {
    if (chosen.size === 0) return;
    setBusy(true);
    setSubmitError(null);
    try {
      const res = await api.admin.clients.autotaskContacts.import(
        client.id,
        Array.from(chosen),
      );
      await onImported();
      toast(
        `${res.created} invitation${res.created === 1 ? "" : "s"} sent` +
          (res.skipped_existing > 0 ? `, ${res.skipped_existing} already had accounts` : ""),
      );
      onClose();
    } catch (e) {
      setSubmitError(errText(e, "Import failed."));
      setBusy(false);
    }
  }

  return (
    <Drawer
      eyebrow={client.name}
      title="Import from Autotask"
      desc={
        notLinked
          ? "This customer is not linked to an Autotask company, so there are no contacts to import."
          : `${importable.length} Autotask contact${importable.length === 1 ? " isn't" : "s aren't"} on the portal yet. Tick who to invite.`
      }
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          {importable.length > 0 && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void run()}
              disabled={busy || chosen.size === 0}
            >
              {busy ? "Inviting…" : `Invite ${chosen.size} selected`}
            </button>
          )}
        </>
      }
    >
      {isLoading && <div className="ad-note">Loading contacts…</div>}
      {error && (
        <div className="form-error">{String((error as Error).message ?? error)}</div>
      )}

      {!isLoading && !error && !notLinked && importable.length === 0 && (
        <Empty
          title="Everyone is already here"
          blurb="Every Autotask contact with an email address already has a portal account."
        />
      )}

      {importable.length > 0 && (
        <>
          <div className="rf-search" style={{ marginBottom: 12, maxWidth: "none" }}>
            <Icon name="search" />
            <input
              type="search"
              value={query}
              placeholder="Filter contacts"
              aria-label="Filter contacts"
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          {filtered.map((c: AutotaskContactCandidate) => (
            <label key={c.autotask_contact_id} className="ad-pick">
              <input
                type="checkbox"
                checked={chosen.has(c.autotask_contact_id)}
                onChange={(e) =>
                  setSelected((prev) => {
                    const next = new Set(prev ?? []);
                    if (e.target.checked) next.add(c.autotask_contact_id);
                    else next.delete(c.autotask_contact_id);
                    return next;
                  })
                }
              />
              <AdAvatar name={c.full_name || c.email || "?"} />
              <span className="bd">
                <span className="t">{c.full_name || "(no name)"}</span>
                <span className="s">{c.email}</span>
              </span>
            </label>
          ))}
        </>
      )}

      {submitError && (
        <div className="form-error" style={{ marginTop: 12 }}>
          {submitError}
        </div>
      )}
    </Drawer>
  );
}
